import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { Mail, X, ShieldCheck, Check, Loader2, MailMinus, AlertCircle } from 'lucide-react'
import type { ChatInboxEmail } from '../../../shared/types/chat'
import { EmailBodyView } from '@tendbot/ui'
import { unsubscribeEmail } from '../../../shared/api/emails'
import UnsubscribeConfirmSheet from './UnsubscribeConfirmSheet'

interface EmailNotificationCardProps {
  email: ChatInboxEmail
}

type UnsubscribeState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'pending' }
  | { kind: 'unsubscribed' }
  | { kind: 'failed'; manualHref: string | null }

export default function EmailNotificationCard({ email }: EmailNotificationCardProps) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)

  const initialUnsubscribed = !!email.unsubscribe?.unsubscribedAt
  const [unsub, setUnsub] = useState<UnsubscribeState>(
    initialUnsubscribed ? { kind: 'unsubscribed' } : { kind: 'idle' },
  )

  const hasBody = !email.loading && (email.body || email.bodyHtml)

  const meta = email.unsubscribe
  // Backend can unsubscribe automatically when we have either RFC 8058
  // one-click (HTTPS POST) or a mailto: target (sent via Resend from the
  // user's @tend.bot agent). Otherwise the user has to open the link.
  const canBackend =
    !!email.id && ((!!meta?.oneClick && !!meta.url) || !!meta?.mailto)
  const manualHref = meta?.url ?? (meta?.mailto ? `mailto:${meta.mailto}` : null)
  const canUnsubscribe =
    unsub.kind !== 'unsubscribed' && (canBackend || !!manualHref)

  const handleConfirm = async () => {
    if (!email.id || !canBackend) {
      // Manual link path — sheet's <a> handles navigation; just close.
      setUnsub({ kind: 'idle' })
      return
    }
    setUnsub({ kind: 'pending' })
    try {
      const result = await unsubscribeEmail(email.id)
      if (result.status === 'unsubscribed') {
        setUnsub({ kind: 'unsubscribed' })
      } else {
        const fallback = result.url ?? (result.mailto ? `mailto:${result.mailto}` : null)
        setUnsub({ kind: 'failed', manualHref: fallback })
      }
    } catch {
      setUnsub({ kind: 'failed', manualHref })
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => hasBody && setOpen(true)}
        disabled={!hasBody}
        className="flex flex-col gap-1 px-3 py-2 rounded-xl border border-slate-light/40 bg-slate-dark/60 enabled:hover:bg-slate-mid/50 enabled:hover:border-slate-light/60 disabled:opacity-70 transition-colors text-left w-full"
      >
        {/* Subject row */}
        <div className="flex items-center gap-2 w-full">
          {email.loading ? (
            <Loader2 size={14} className="text-pearl/50 flex-shrink-0 animate-spin" />
          ) : (
            <Mail size={14} className="text-pearl/50 flex-shrink-0" />
          )}
          <span className="text-sm text-cream truncate flex-1">
            {email.loading ? t('email.loading') : email.subject}
          </span>
          {!email.status && !email.loading && (
            <span className="text-xs text-pearl/60 flex-shrink-0">{t('email.tapToView')}</span>
          )}
        </div>

        {/* Status row */}
        {email.status === 'processing' && (
          <div className="flex items-center gap-1.5 pl-[22px]">
            <Loader2 size={12} className="text-amber/70 animate-spin" />
            <span className="text-xs text-amber/70">{t('email.processing')}</span>
          </div>
        )}
        {email.status === 'no_action' && (
          <div className="flex items-center gap-1.5 pl-[22px]">
            <Check size={12} className="text-emerald/70" />
            <span className="text-xs text-emerald/70">{t('email.noAction')}</span>
          </div>
        )}
      </button>

      {/* Unsubscribe action / status */}
      {(canUnsubscribe || unsub.kind === 'unsubscribed' || unsub.kind === 'failed') && (
        <div className="flex items-center gap-2 px-1 mt-1.5">
          {unsub.kind === 'unsubscribed' && (
            <span className="flex items-center gap-1.5 text-xs text-emerald/80">
              <Check size={12} />
              {t('email.unsubscribe.done')}
            </span>
          )}
          {unsub.kind === 'failed' && (
            <>
              <span className="flex items-center gap-1.5 text-xs text-rose/80">
                <AlertCircle size={12} />
                {t('email.unsubscribe.failed')}
              </span>
              {unsub.manualHref && (
                <a
                  href={unsub.manualHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gold/80 hover:text-gold underline decoration-gold/40 underline-offset-2"
                >
                  {t('email.unsubscribe.openLink')}
                </a>
              )}
            </>
          )}
          {(unsub.kind === 'idle' || unsub.kind === 'pending') && canUnsubscribe && (
            <button
              type="button"
              onClick={() => setUnsub({ kind: 'confirming' })}
              disabled={unsub.kind === 'pending'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-pearl/20 text-pearl/70 text-xs hover:bg-pearl/5 hover:border-pearl/30 hover:text-pearl/90 transition-colors disabled:opacity-60"
            >
              {unsub.kind === 'pending' ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <MailMinus size={12} />
              )}
              {t('email.unsubscribe.action')}
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center modal-backdrop bg-black/60"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-charcoal border-t border-slate-light/30 rounded-t-2xl p-5 pb-8 max-h-[80dvh] flex flex-col overscroll-contain sm:border sm:rounded-2xl sm:pb-5 sm:max-h-[85dvh] sm:shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-ivory truncate">{email.subject}</h3>
                  <p className="text-xs text-pearl/50 mt-0.5">
                    {t('email.from')} {email.sender} &lt;{email.senderEmail}&gt;
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="ml-3 p-1.5 rounded-lg text-pearl/60 hover:text-ivory hover:bg-white/5 transition-colors flex-shrink-0"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Verified badge */}
              {email.verified && (
                <div className="flex items-center gap-1.5 mb-3 text-emerald/70">
                  <ShieldCheck size={13} />
                  <span className="text-xs">{t('email.verifiedSender')}</span>
                </div>
              )}

              {/* Divider */}
              <div className="h-px bg-slate-light/30 mb-4" />

              <EmailBodyView
                bodyHtml={email.bodyHtml}
                bodyText={email.body}
                title={email.subject}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {unsub.kind === 'confirming' && (
          <UnsubscribeConfirmSheet
            sender={email.sender || email.senderEmail}
            automatic={canBackend}
            busy={false}
            manualHref={manualHref}
            onClose={() => setUnsub({ kind: 'idle' })}
            onConfirm={handleConfirm}
          />
        )}
      </AnimatePresence>
    </>
  )
}
